import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Permission, UserRole } from '@takumi/shared';
import { createContext, useCallback, useContext, useEffect, type ReactNode } from 'react';
import { api, ApiError, setCsrfToken, setUnauthorizedHandler } from './api';

export interface Me {
  user: { id: string; email: string; fullName: string; role: UserRole };
  permissions: Permission[];
  csrfToken: string;
}

interface AuthState {
  me: Me | null;
  loading: boolean;
  can: (p: Permission) => boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.get<Me>('/auth/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

  useEffect(() => {
    setCsrfToken(data?.csrfToken ?? null);
  }, [data]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setCsrfToken(null);
      qc.setQueryData(['me'], null);
    });
  }, [qc]);

  const login = useCallback(
    async (email: string, password: string) => {
      const me = await api.post<Me>('/auth/login', { email, password });
      setCsrfToken(me.csrfToken);
      // Drop cached data from any previous session, but keep the observed ['me'] query alive.
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
      qc.setQueryData(['me'], me);
    },
    [qc],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setCsrfToken(null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
      qc.setQueryData(['me'], null);
    }
  }, [qc]);

  const me = data ?? null;
  const can = useCallback((p: Permission) => !!me?.permissions.includes(p), [me]);

  return <AuthContext.Provider value={{ me, loading: isLoading, can, login, logout }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
